"use client";

import React, { useState, useEffect, useRef } from "react";
import { BrowserMultiFormatReader, DecodeHintType, BarcodeFormat, NotFoundException } from "@zxing/library";
import { Button } from "./button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./dialog";
import { ScanLine, X, Check, AlertCircle, Camera } from "lucide-react";

interface BarcodeScannerInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onError?: (error: string) => void;
  disabled?: boolean;
}

export function BarcodeScannerInput({
  value = "",
  onChange,
  onError,
  disabled = false,
}: BarcodeScannerInputProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [scannedValue, setScannedValue] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);

  const codeReaderRef = useRef<BrowserMultiFormatReader | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isInitialized = useRef(false);

  // Initialize scanner and auto-start when modal opens
  useEffect(() => {
    if (isModalOpen && !isInitialized.current) {
      const initScanner = async () => {
        try {
          // Configure hints for optimal PDF417 and general barcode detection
          const hints = new Map();

          // Specify all barcode formats to support
          hints.set(DecodeHintType.POSSIBLE_FORMATS, [
            BarcodeFormat.PDF_417,        // South African license discs
            BarcodeFormat.QR_CODE,
            BarcodeFormat.EAN_13,
            BarcodeFormat.EAN_8,
            BarcodeFormat.UPC_A,
            BarcodeFormat.UPC_E,
            BarcodeFormat.CODE_128,
            BarcodeFormat.CODE_39,
            BarcodeFormat.CODE_93,
            BarcodeFormat.CODABAR,
            BarcodeFormat.ITF,
            BarcodeFormat.DATA_MATRIX,
            BarcodeFormat.AZTEC,
          ]);

          // Note: Not using TRY_HARDER or PURE_BARCODE as they can be too restrictive
          // for camera-based scanning. ZXing handles PDF417 well with default settings.

          // Create reader with hints
          const codeReader = new BrowserMultiFormatReader(hints);
          codeReaderRef.current = codeReader;
          isInitialized.current = true;

          // Auto-start scanning
          await startScanning();
        } catch (err) {
          console.error("Error initializing scanner:", err);
          setError("Failed to initialize scanner. Please try again.");
          onError?.("Failed to initialize scanner");
        }
      };

      // Small delay to ensure DOM is ready
      const timer = setTimeout(initScanner, 100);
      return () => clearTimeout(timer);
    }
  }, [isModalOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, []);

  const startScanning = async () => {
    if (!codeReaderRef.current || isScanning || !videoRef.current) return;

    setError(null);
    setScannedValue(null);

    try {
      // Minimal constraints for better Android compatibility
      // Let the device choose optimal resolution
      const constraints = {
        video: {
          facingMode: { ideal: "environment" } // Prefer back camera
        }
      };

      // Manual MediaStream setup for better Android compatibility
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      if (!videoRef.current) {
        // Stop stream if component unmounted during async operation
        stream.getTracks().forEach(track => track.stop());
        return;
      }

      videoRef.current.srcObject = stream;
      videoRef.current.setAttribute("playsinline", "true");

      // Wait for video to load before starting decoding
      await videoRef.current.play();

      // Use decodeContinuously for continuous scanning
      codeReaderRef.current.decodeContinuously(
        videoRef.current,
        (result, err) => {
          if (result) {
            // Barcode successfully decoded
            const decodedText = result.getText();
            handleScanSuccess(decodedText);
          }

          if (err) {
            // NotFoundException is normal when no barcode is found yet
            if (err.name !== "NotFoundException") {
              console.error("Scanning error:", err);
              // Don't show errors for normal scanning attempts
            }
          }
        }
      );

      setIsScanning(true);
      setHasPermission(true);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      const errorStr = errorMsg.toLowerCase();

      // Handle different error types
      if (errorStr.includes("permission") || errorStr.includes("notallowed")) {
        setError("Camera permission denied. Please allow camera access and try again.");
        setHasPermission(false);
      } else if (errorStr.includes("notfound") || errorStr.includes("no camera")) {
        setError("No camera found. Please ensure your device has a camera.");
      } else if (errorStr.includes("notreadable") || errorStr.includes("in use")) {
        setError("Camera is already in use by another application.");
      } else {
        setError(`Failed to start scanner: ${errorMsg}`);
      }

      setIsScanning(false);
      onError?.(errorMsg);
      console.error("Scanner start error:", err);
    }
  };

  const stopScanning = () => {
    if (codeReaderRef.current) {
      try {
        codeReaderRef.current.reset();
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }

    // Manually stop and clean up MediaStream tracks
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }

    setIsScanning(false);
  };

  const handleScanSuccess = (decodedText: string) => {
    setScannedValue(decodedText);
    // Auto-stop scanning after successful scan
    stopScanning();
  };

  const handleConfirm = () => {
    if (scannedValue) {
      onChange?.(scannedValue);
      handleClose();
    }
  };

  const handleClose = () => {
    stopScanning();

    // Reset scanner state
    if (codeReaderRef.current) {
      try {
        codeReaderRef.current.reset();
      } catch (err) {
        console.error("Error resetting scanner:", err);
      }
      codeReaderRef.current = null;
    }

    isInitialized.current = false;
    setIsModalOpen(false);
    setScannedValue(null);
    setError(null);
    setHasPermission(null);
  };

  const openScanner = () => {
    if (!disabled) {
      setIsModalOpen(true);
      setError(null);
      setScannedValue(null);
    }
  };

  return (
    <>
      {/* Trigger Button */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          {value && (
            <div className="flex-1 px-3 py-2 text-sm border rounded-md bg-gray-50 dark:bg-gray-900 border-gray-300 dark:border-gray-700">
              {value}
            </div>
          )}
          <Button
            type="button"
            onClick={openScanner}
            disabled={disabled}
            variant={value ? "outline" : "default"}
            size="default"
            className="flex items-center gap-2"
          >
            <ScanLine className="w-4 h-4" />
            {value ? "Rescan" : "Scan Barcode"}
          </Button>
        </div>
      </div>

      {/* Scanner Modal */}
      <Dialog open={isModalOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg w-full p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Scan Barcode
            </DialogTitle>
          </DialogHeader>

          <div className="relative bg-black">
            {/* Video Element for Camera */}
            <video
              ref={videoRef}
              className="w-full min-h-[400px] object-cover"
              autoPlay
              playsInline
              muted
            />

            {/* Scan Guide Overlay - Horizontal rectangle optimized for PDF417 */}
            {isScanning && !scannedValue && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="relative">
                  {/* Scanning frame */}
                  <div className="w-[300px] h-[150px] border-4 border-white/80 rounded-lg shadow-lg">
                    {/* Corner indicators */}
                    <div className="absolute -top-1 -left-1 w-8 h-8 border-t-4 border-l-4 border-green-500"></div>
                    <div className="absolute -top-1 -right-1 w-8 h-8 border-t-4 border-r-4 border-green-500"></div>
                    <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-4 border-l-4 border-green-500"></div>
                    <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-4 border-r-4 border-green-500"></div>
                  </div>
                  {/* Instruction text */}
                  <p className="text-white text-sm mt-4 text-center bg-black/60 px-3 py-2 rounded">
                    Align barcode within frame
                  </p>
                </div>
              </div>
            )}

            {/* Loading State */}
            {!isScanning && !scannedValue && !error && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                <div className="text-center text-white">
                  <Camera className="w-12 h-12 mx-auto mb-2 animate-pulse" />
                  <p className="text-sm">Initializing camera...</p>
                </div>
              </div>
            )}

            {/* Scanned Value Display */}
            {scannedValue && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 m-4 max-w-sm w-full space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-green-100 dark:bg-green-900 rounded-full">
                      <Check className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                        Barcode Detected
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 break-all font-mono">
                        {scannedValue}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleConfirm}
                      className="flex-1"
                      size="lg"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Confirm
                    </Button>
                    <Button
                      onClick={async () => {
                        setScannedValue(null);
                        await startScanning();
                      }}
                      variant="outline"
                      className="flex-1"
                      size="lg"
                    >
                      <ScanLine className="w-4 h-4 mr-2" />
                      Scan Again
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 m-4 max-w-sm w-full space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-red-100 dark:bg-red-900 rounded-full">
                      <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                        Scanner Error
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {error}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {hasPermission !== false && (
                      <Button
                        onClick={async () => {
                          setError(null);
                          await startScanning();
                        }}
                        className="flex-1"
                        size="lg"
                      >
                        Try Again
                      </Button>
                    )}
                    <Button
                      onClick={handleClose}
                      variant="outline"
                      className="flex-1"
                      size="lg"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer with Close Button */}
          {isScanning && (
            <div className="px-6 py-4 border-t bg-gray-50 dark:bg-gray-900/50">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Position barcode within the frame
                </p>
                <Button onClick={handleClose} variant="outline" size="sm">
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
